import { FC, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionPrivacy,
	ActionSubmit,
	ActionTags,
	ActionText,
	ActionValidator,
	useBaseModules,
} from '@bonusly/common-action-input';
import { Box, Checkbox, Flex, Hide, ModalBody, ModalFooter, rem, Text } from '@bonusly/design-system';

import { RecipientSelectorWithCsvUpload } from '../../../../../../components/action-input/components/recipient/recipient-selector-with-csv-upload';
import { useCurrentCompany } from '../../../../../../lib/who-am-i';
import { Award } from '../../../../../../types/bonus';
import { AwardHeader } from './AwardHeader';
import { useAwardContent } from './hooks/useAwardContent';
import { useAwardModal } from './hooks/useAwardModal';
import { useSharedAwardConfig } from './hooks/useSharedAwardConfig';

type OldEditableAwardInputProps = {
	award: Award;
	hideGiver: boolean;
	setHideGiver: (hideGiver: boolean) => void;
	setIsSubmitting: (isSubmitting: boolean) => void;
};

export const OldEditableAwardInput: FC<OldEditableAwardInputProps> = ({ award, hideGiver, setHideGiver, setIsSubmitting }) => {
	const { onClose } = useAwardModal();
	const { t } = useTranslation('recognition');
	const content = useAwardContent();

	const config = useSharedAwardConfig({ award, hideGiver, isTemplatedAward: false, onSuccess: onClose });
	const company = useCurrentCompany();

	const modules = useBaseModules({
		includeAmount: true,
		includeEmoji: true,
		includeHashtag: true,
		includeLink: true,
		includeMention: true,
		includeRecipient: true,
		includePrivacy: true,
		includeGif: true,
		includeAttachment: true,
		includeVideoRecording: true,
	});

	const validator = useMemo(() => {
		return new ActionValidator({
			requireRecipient: true,
			requirePositiveAmount: !company.zero_point_bonuses,
		});
	}, [company]);

	useEffect(() => {
		setIsSubmitting(config.isSubmitting);
	}, [config.isSubmitting, setIsSubmitting]);

	return (
		<ActionInput
			autofocus="end"
			content={content}
			context="non-templated-award"
			modules={modules}
			placeholder={t('awards.give_modal.input_placeholder_text')}
			validator={validator}
			options={{
				recipient: {
					component: RecipientSelectorWithCsvUpload,
				},
			}}
			{...config}
		>
			<ModalBody pt={4}>
				<AwardHeader award={award} mb={4} />

				<Flex alignItems="flex-start" justifyContent="space-between">
					<ActionTags />
					<Hide below="xs">
						<ActionPrivacy />
					</Hide>
				</Flex>

				<Box mb={2}>
					<ActionText inputStyles={{ minHeight: rem(150) }} />
					<ActionFeedback />
					<Checkbox isChecked={!hideGiver} onChange={(event) => setHideGiver(!event.target.checked)}>
						<Text fontSize={100} mt={1}>
							{t('awards.give_modal.show_giver_label')}
						</Text>
					</Checkbox>
				</Box>

				<ActionMedia w="full" />
			</ModalBody>

			<ModalFooter borderTop="1px solid" borderColor="gray.100">
				<Flex
					alignItems={{ base: 'stretch', xs: 'flex-start' }}
					direction={{ base: 'column', xs: 'row' }}
					gap={2}
					justifyContent="space-between"
					mb={2}
					w="full"
				>
					<Flex justifyContent="space-between" flexGrow={1}>
						<ActionDecorations />
						<Hide above="xs">
							<ActionPrivacy />
						</Hide>
					</Flex>

					<ActionSubmit size="lg" />
				</Flex>
			</ModalFooter>
		</ActionInput>
	);
};
